import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ProductDietaryAttributeEntry, emptyDietaryAttributeRow } from '../types';
import { CodeNameOption, DietaryAttributeMasterData } from '../../../../app/services/api/orderManagementClient';
import {
  DIETARY_ATTRIBUTE_CATEGORY_LABEL_KEYS,
  DIETARY_ATTRIBUTE_STATUS_LABEL_KEYS,
  ASSESSMENT_SOURCE_TYPE_LABEL_KEYS,
  getEnumLabel,
} from '../enumLabels';

interface DietaryAttributesTabProps {
  dietaryAttributes: ProductDietaryAttributeEntry[];
  onChange: (dietaryAttributes: ProductDietaryAttributeEntry[]) => void;
  disabled?: boolean;
  referenceOptions?: DietaryAttributeMasterData | null;
}

const toDateValue = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const DietaryAttributesTab: FC<DietaryAttributesTabProps> = ({ dietaryAttributes, onChange, disabled, referenceOptions }) => {
  const { t } = useTranslation('productManagement');
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const updateRow = (index: number, patch: Partial<ProductDietaryAttributeEntry>) => {
    const next = dietaryAttributes.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (index: number) => {
    // Row identity is positional (key={index}); reset expanded state so a delete
    // can't leave a later row's expanded panel misattributed to its new position.
    setExpandedRows({});
    onChange(dietaryAttributes.filter((_, i) => i !== index));
  };

  const addRow = () => {
    // Expand the new row by default so the optional certification/source/date fields inside the
    // collapsed section are visible without an extra click. Mandatory fields (Code/Status) are
    // always visible in the top row regardless of expansion state.
    setExpandedRows((prev) => ({ ...prev, [dietaryAttributes.length]: true }));
    onChange([...dietaryAttributes, emptyDietaryAttributeRow()]);
  };

  const toggleExpanded = (index: number) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="textLG"
          component="div"
          sx={{ fontWeight: 600 }}>
          {t('dietaryAttributesInfo', 'Dietary Attributes')}
        </Typography>
        <Button startIcon={<AddIcon />}
          onClick={addRow}
          disabled={disabled}
          size="md">
          {t('addDietaryAttribute', 'Add dietary attribute')}
        </Button>
      </Box>

      {dietaryAttributes.length === 0 && (
        <Typography variant="textSM"
          component="div"
          color="text.secondary"
          sx={{ mb: 3 }}>
          {t('noDietaryAttributesCaptured', 'No dietary attributes captured yet.')}
        </Typography>
      )}

      {dietaryAttributes.map((row, index) => {
        const isExpanded = Boolean(expandedRows[index]);
        return (
          <Box key={index}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, pt: '12px', pr: 3, pb: 0, pl: 3, mb: 3 }}>
            <Grid container
              spacing={3}
              alignItems="center">
              <Grid item
                xs={12}
                sm={3}>
                {referenceOptions ? (
                  <Autocomplete<CodeNameOption>
                    options={referenceOptions.dietaryAttributes}
                    getOptionLabel={(option) => option.code}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    value={referenceOptions.dietaryAttributes.find((d) => d.code === row.dietaryAttributeCode) ?? null}
                    onChange={(_e, newValue) => updateRow(index, {
                      dietaryAttributeCode: newValue?.code ?? '',
                      dietaryAttributeName: newValue?.name ?? '',
                    })}
                    disabled={disabled}
                    size="small"
                    renderOption={(props, option) => (
                      <li {...props} 
                        key={option.code}>{option.code}</li>
                    )}
                    renderInput={(params) => <TextField {...params} 
                      label={t('dietaryAttributeCode', 'Code')} 
                      required />}
                  />
                ) : (
                  <TextField
                    label={t('dietaryAttributeCode', 'Code')}
                    value={row.dietaryAttributeCode ?? ''}
                    onChange={(e) => updateRow(index, { dietaryAttributeCode: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                )}
              </Grid>
              <Grid item
                xs={12}
                sm={3}>
                <TextField
                  label={t('dietaryAttributeName', 'Name')}
                  value={row.dietaryAttributeName ?? ''}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item
                xs={12}
                sm={3}>
                {referenceOptions ? (
                  <Autocomplete<string>
                    options={referenceOptions.categories}
                    getOptionLabel={(option) => getEnumLabel(t, DIETARY_ATTRIBUTE_CATEGORY_LABEL_KEYS, option)}
                    value={row.dietaryAttributeCategory || null}
                    onChange={(_e, newValue) => updateRow(index, { dietaryAttributeCategory: newValue ?? '' })}
                    disabled={disabled}
                    size="small"
                    renderInput={(params) => <TextField {...params} 
                      label={t('dietaryAttributeCategory', 'Category')} />}
                  />
                ) : (
                  <TextField
                    label={t('dietaryAttributeCategory', 'Category')}
                    value={row.dietaryAttributeCategory ?? ''}
                    onChange={(e) => updateRow(index, { dietaryAttributeCategory: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                )}
              </Grid>
              <Grid item
                xs={8}
                sm={2}>
                {referenceOptions ? (
                  <Autocomplete<string>
                    options={referenceOptions.statuses}
                    getOptionLabel={(option) => getEnumLabel(t, DIETARY_ATTRIBUTE_STATUS_LABEL_KEYS, option)}
                    value={row.dietaryAttributeStatus || null}
                    onChange={(_e, newValue) => updateRow(index, { dietaryAttributeStatus: newValue ?? '' })}
                    disabled={disabled}
                    size="small"
                    renderInput={(params) => <TextField {...params} 
                      label={t('dietaryAttributeStatus', 'Status')} 
                      required />}
                  />
                ) : (
                  <TextField
                    label={t('dietaryAttributeStatus', 'Status')}
                    value={row.dietaryAttributeStatus ?? ''}
                    onChange={(e) => updateRow(index, { dietaryAttributeStatus: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                )}
              </Grid>
              <Grid item
                xs={4}
                sm={1}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton size="small"
                  onClick={() => toggleExpanded(index)}
                  aria-label={isExpanded ? t('collapse', 'Collapse') : t('expand', 'Expand')}>
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small"
                  onClick={() => removeRow(index)}
                  disabled={disabled}
                  aria-label={t('remove', 'Remove')}>
                  <DeleteOutlineIcon fontSize="small"
                    color='error' />
                </IconButton>
              </Grid>
            </Grid>

            <Collapse in={isExpanded}>
              <Grid container
                spacing={3}
                sx={{ mt: 0, pt: 0, pb: '12px', pl: '12px', pr: '12px' }}>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('certificationBody', 'Certification Body')}
                    value={row.certificationBody ?? ''}
                    onChange={(e) => updateRow(index, { certificationBody: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('certificationReference', 'Certification Reference')}
                    value={row.certificationReference ?? ''}
                    onChange={(e) => updateRow(index, { certificationReference: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  {referenceOptions ? (
                    <Autocomplete<string>
                      options={referenceOptions.sourceTypes}
                      getOptionLabel={(option) => getEnumLabel(t, ASSESSMENT_SOURCE_TYPE_LABEL_KEYS, option)}
                      value={row.sourceType || null}
                      onChange={(_e, newValue) => updateRow(index, { sourceType: newValue ?? '' })}
                      disabled={disabled}
                      size="small"
                      renderInput={(params) => <TextField {...params} 
                        label={t('sourceType', 'Source Type')} />}
                    />
                  ) : (
                    <TextField
                      label={t('sourceType', 'Source Type')}
                      value={row.sourceType ?? ''}
                      onChange={(e) => updateRow(index, { sourceType: e.target.value })}
                      disabled={disabled}
                      size="small"
                      fullWidth
                    />
                  )}
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('sourceReference', 'Source Reference')}
                    value={row.sourceReference ?? ''}
                    onChange={(e) => updateRow(index, { sourceReference: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <FormControl fullWidth
                    size="small">
                    <InputLabel shrink>{t('supplierConfirmed', 'Supplier Confirmed')}</InputLabel>
                    <Select
                      value={row.supplierConfirmed ?? ''}
                      onChange={(e: SelectChangeEvent) => updateRow(index, { supplierConfirmed: e.target.value })}
                      displayEmpty
                      disabled={disabled}
                      size="small"
                      fullWidth
                      label={t('supplierConfirmed', 'Supplier Confirmed')}
                      notched
                    >
                      <MenuItem value=""><em>{t('none', 'None')}</em></MenuItem>
                      <MenuItem value="TRUE">{t('yes', 'Yes')}</MenuItem>
                      <MenuItem value="FALSE">{t('no', 'No')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveFrom', 'Effective From')}
                    value={toDateValue(row.effectiveFrom)}
                    onChange={(newValue) => updateRow(index, { effectiveFrom: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    maxDate={toDateValue(row.effectiveTo) ?? undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true }, field: { clearable: true } }}
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveTo', 'Effective To')}
                    value={toDateValue(row.effectiveTo)}
                    onChange={(newValue) => updateRow(index, { effectiveTo: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    minDate={toDateValue(row.effectiveFrom) ?? undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true }, field: { clearable: true } }}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    label={t('notes', 'Notes')}
                    value={row.notes ?? ''}
                    onChange={(e) => updateRow(index, { notes: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    multiline
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default DietaryAttributesTab;
